<script setup lang="ts">
import { twMerge } from 'tailwind-merge';

import { SaleSteppedPrice } from '@lib/shared';

// PROPS
interface Props {
    disabled?: boolean;
    minValue?: number;
    maxValue?: number;
    steppedLimits?: boolean; // Apply step rounding on minValue/maxValue
    priceClass?: string;
}

const { disabled = false, minValue = 0, maxValue = Infinity, steppedLimits = false, priceClass } = defineProps<Props>();

// EVENTS
const emit = defineEmits<{
    decrement: [number];
    increment: [number];
}>();

// MODEL
const model = defineModel<number>();

// COMPOSABLES
const { tn, tt } = useTypedI18n();

// DATA
const saleSteppedPrice = computed(() => SaleSteppedPrice.createDefault());

const min = computed(() => {
    return steppedLimits ? saleSteppedPrice.value.getStepRoundedReservePrice(minValue) : minValue;
});

const max = computed(() => {
    return steppedLimits ? saleSteppedPrice.value.getStepRoundedAmount(maxValue) : maxValue;
});

const minusDisabledByValue = computed(() => model.value !== undefined && model.value <= min.value);

const plusDisabledByValue = computed(() => model.value !== undefined && model.value >= max.value);

const formattedValue = computed(() => (model.value != null ? tn(model.value, 'currency') : ''));

const priceMergedClass = twMerge('text-primary text-3xl font-semibold', priceClass);

// METHODS
const onUpdatePrice = (steps: number): void => {
    const unlimitedValue = saleSteppedPrice.value.getDeltaStepsAmount(saleSteppedPrice.value.getStepRoundedReservePrice(model.value ?? 0), steps);

    model.value = Math.min(Math.max(unlimitedValue, min.value), max.value);
};

const minusHold = usePressHold({
    onTrigger: () => {
        if (!minusDisabledByValue.value) onUpdatePrice(-1);
    },
    onStop: () => {
        if (model.value !== undefined) emit('decrement', model.value);
    },
});

const plusHold = usePressHold({
    onTrigger: () => {
        if (!plusDisabledByValue.value) onUpdatePrice(1);
    },
    onStop: () => {
        if (model.value !== undefined) emit('increment', model.value);
    },
});

const showHint = computed(() => minusHold.showHint.value || plusHold.showHint.value);
</script>

<template>
    <div class="flex flex-1 items-center justify-between">
        <UButton
            icon="lucide:minus"
            variant="subtle"
            :disabled="disabled || minusDisabledByValue"
            size="sm"
            @pointerdown="minusHold.onPointerDown"
            @pointerup="minusHold.stop"
            @pointerleave="minusHold.stop"
            @pointercancel="minusHold.stop"
        />
        <UTooltip :text="tt('sale.pressHoldHint')" :open="showHint">
            <div class="flex flex-col items-center">
                <span :class="priceMergedClass">{{ formattedValue }}</span>
                <slot name="belowPrice" />
            </div>
        </UTooltip>
        <UButton
            icon="lucide:plus"
            variant="subtle"
            :disabled="disabled || plusDisabledByValue"
            size="sm"
            @pointerdown="plusHold.onPointerDown"
            @pointerup="plusHold.stop"
            @pointerleave="plusHold.stop"
            @pointercancel="plusHold.stop"
        />
    </div>
</template>
